import type { InternalLoggerLevel } from '@grafana/faro-core';

import { type SystemDateFormatSettings } from '../datetime/formats';
import { type MapLayerOptions } from '../geo/layer';

import { type DataSourceInstanceSettings } from './datasource';
import { type FeatureToggles } from './featureToggles.gen';
import { type IconName } from './icon';
import { type NavLinkDTO } from './navModel';
import { type OrgRole } from './orgs';
import { type PanelPluginMeta } from './panel';
import { type AngularMeta, type PluginDependencies, type PluginExtensions, type PluginLoadingStrategy } from './plugin';
import { type TimeOption } from './time';

export interface AzureSettings {
  cloud?: string;
  clouds?: AzureCloudInfo[];
  managedIdentityEnabled: boolean;
  workloadIdentityEnabled: boolean;
  userIdentityEnabled: boolean;
  userIdentityFallbackCredentialsEnabled: boolean;
  azureEntraPasswordCredentialsEnabled: boolean;
}

export interface AzureCloudInfo {
  name: string;
  displayName: string;
}

export type AppPluginConfig = {
  id: string;
  path: string;
  version: string;
  preload: boolean;
  /** @deprecated it will be removed in a future release */
  angular: AngularMeta;
  loadingStrategy: PluginLoadingStrategy;
  dependencies: PluginDependencies;
  extensions: PluginExtensions;
  moduleHash?: string;
  buildMode?: string;
};

export type PreinstalledPlugin = {
  id: string;
  version: string;
};

/**
 * Describes the build information that will be available via the Grafana configuration.
 *
 * @public
 */
export interface BuildInfo {
  // This MUST be a semver-ish version string, such as "11.0.0-54321"
  version: string;
  // Version to show in the UI instead of version
  versionString: string;
  buildstamp: number;
  commit: string;
  commitShort: string;
  env: string;
  edition: GrafanaEdition;
  latestVersion: string;
  hasUpdate: boolean;
  hideVersion: boolean;
}

/**
 * @internal
 */
export enum GrafanaEdition {
  OpenSource = 'Open Source',
  Pro = 'Pro',
  Enterprise = 'Enterprise',
  Trial = 'Cloud Trial',
}

/**
 * Describes the license information about the current running instance of Grafana.
 *
 * @public
 */
export interface LicenseInfo {
  expiry: number;
  licenseUrl: string;
  stateInfo: string;
  edition: GrafanaEdition;
  enabledFeatures: { [key: string]: boolean };
  trialExpiry?: number;
}

/**
 * Describes GrafanaJavascriptAgentConfig integration config
 *
 * @public
 */
export interface GrafanaJavascriptAgentConfig {
  enabled: boolean;
  customEndpoint: string;
  apiKey: string;
  internalLoggerLevel: InternalLoggerLevel;

  consoleInstrumentalizationEnabled: boolean;
  performanceInstrumentalizationEnabled: boolean;
  cspInstrumentalizationEnabled: boolean;
  tracingInstrumentalizationEnabled: boolean;
  botFilterEnabled: boolean;
  trackResources?: boolean;
}

interface UnifiedAlertingStateHistoryConfig {
  backend?: string;
  primary?: string;
  prometheusTargetDatasourceUID?: string;
  prometheusMetricName?: string;
}

export interface UnifiedAlertingConfig {
  minInterval: string;
  stateHistory?: UnifiedAlertingStateHistoryConfig;
  recordingRulesEnabled?: boolean;
  defaultRecordingRulesTargetDatasourceUID?: string;

  // Backward compatibility aliases - deprecated
  /** @deprecated Use stateHistory.backend instead */
  alertStateHistoryBackend?: string;
  /** @deprecated Use stateHistory.primary instead */
  alertStateHistoryPrimary?: string;
}

/** Supported OAuth services
 *
 * @public
 */
export type OAuth =
  | 'github'
  | 'gitlab'
  | 'google'
  | 'generic_oauth'
  // | 'grafananet' Deprecated. Key always changed to "grafana_com"
  | 'grafana_com'
  | 'azuread'
  | 'okta';

/** Map of enabled OAuth services and their respective names
 *
 * @public
 */
export type OAuthSettings = Partial<Record<OAuth, { name: string; icon?: IconName }>>;

/**
 * Information needed for analytics providers
 *
 * @internal
 */
export interface AnalyticsSettings {
  identifier: string;
  intercomIdentifier?: string;
}

/**
 * Current user info included in bootData.
 * Corresponds to `window.grafanaBootData.user`
 * @internal
 */
export interface CurrentUserDTO {
  isSignedIn: boolean;
  id: number;
  uid: string;
  externalUserId: string;
  login: string;
  email: string;
  name: string;
  theme: string; // dark | light | system
  orgCount: number;
  orgId: number;
  orgName: string;
  orgRole: OrgRole | '';
  isGrafanaAdmin: boolean;
  gravatarUrl: string;
  timezone: string;
  weekStart: string;
  language: string;
  permissions?: Record<string, boolean>;
  analytics: AnalyticsSettings;
  authenticatedBy: string;

  /** @deprecated Use theme instead */
  lightTheme: boolean;
}

/**
 * Contains essential user and config info.
 * Corresponds to `window.grafanaBootData`.
 * @internal
 */
export interface BootData {
  user: CurrentUserDTO;
  settings: GrafanaConfig;
  navTree: NavLinkDTO[];
  assets: {
    light: string;
    dark: string;
  };

  /** @deprecated Internal Grafana usage only. This property will be removed at any time. */
  _femt?: boolean;
}

/**
 * Describes all the different Grafana configuration values available for an instance.
 * Corresponds to `window.grafanaBootData.settings`.
 * If you want to access these values, use the `config` object from `@grafana/runtime`.
 * @internal
 */
export interface GrafanaConfig {
  publicDashboardAccessToken: string;
  publicDashboardsEnabled: boolean;
  snapshotEnabled: boolean;
  datasources: { [str: string]: DataSourceInstanceSettings };
  /** @deprecated it will be removed in a future release */
  panels: { [key: string]: PanelPluginMeta };
  /** @deprecated it will be removed in a future release */
  apps: Record<string, AppPluginConfig>;
  auth: AuthSettings;
  minRefreshInterval: string;
  appUrl: string;
  appSubUrl: string;
  azure: AzureSettings;
  jwtHeaderName: string;
  jwtUrlLogin: boolean;
  windowTitlePrefix: string;
  buildInfo: BuildInfo;
  externalUserMngLinkUrl: string;
  externalUserMngLinkName: string;
  externalUserMngInfo: string;
  externalUserMngAnalytics: boolean;
  externalUserMngAnalyticsParams: string;
  externalUserUpgradeLinkUrl: string;
  allowOrgCreate: boolean;
  disableLoginForm: boolean;
  defaultDatasource: string;
  defaultDatasourceManageAlertsUIToggle: boolean;
  defaultAllowRecordingRulesTargetAlertsUIToggle: boolean;
  authProxyEnabled: boolean;
  exploreEnabled: boolean;
  queryHistoryEnabled: boolean;
  helpEnabled: boolean;
  profileEnabled: boolean;
  newsFeedEnabled: boolean;
  ldapEnabled: boolean;
  sigV4AuthEnabled: boolean;
  azureAuthEnabled: boolean;
  samlEnabled: boolean;
  samlName: string;
  awsAllowedAuthProviders: string[];
  awsAssumeRoleProvided: boolean;
  autoAssignOrg: boolean;
  verifyEmailEnabled: boolean;
  oauth: OAuthSettings;
  /** @deprecated always set to true. */
  rbacEnabled: boolean;
  disableUserSignUp: boolean;
  loginHint: string;
  passwordHint: string;
  loginError?: string;
  viewersCanEdit: boolean;
  disableSanitizeHtml: boolean;
  trustedTypesDefaultPolicyEnabled: boolean;
  cspReportOnlyEnabled: boolean;
  expressionsEnabled: boolean;
  liveEnabled: boolean;
  liveMessageSizeLimit: number;
  liveNamespaced: boolean; // use namespace or orgId prefix
  anonymousEnabled: boolean;
  anonymousDeviceLimit: number;
  featureToggles: FeatureToggles;
  licenseInfo: LicenseInfo;
  http2Enabled: boolean;
  dateFormats?: SystemDateFormatSettings;
  grafanaJavascriptAgent: GrafanaJavascriptAgentConfig;
  geomapDefaultBaseLayerConfig?: MapLayerOptions;
  geomapDisableCustomBaseLayer: boolean;
  unifiedAlertingEnabled: boolean;
  unifiedAlerting: UnifiedAlertingConfig;
  feedbackLinksEnabled: boolean;
  supportBundlesEnabled: boolean;
  secureSocksDSProxyEnabled: boolean;
  enableFrontendSandboxForPlugins: string[];
  googleAnalyticsId: string;
  googleAnalytics4Id: string;
  googleAnalytics4SendManualPageViews: boolean;
  rudderstackWriteKey: string;
  rudderstackDataPlaneUrl: string;
  rudderstackSdkUrl: string;
  rudderstackV3SdkUrl: string;
  rudderstackConfigUrl: string;
  rudderstackIntegrationsUrl: string;
  applicationInsightsConnectionString: string;
  applicationInsightsEndpointUrl: string;
  applicationInsightsAutoRouteTracking: boolean;
  analyticsConsoleReporting: boolean;
  pluginImportTelemetryPackages: string[];
  rendererAvailable: boolean;
  rendererVersion: string;
  rendererDefaultImageWidth: number;
  rendererDefaultImageHeight: number;
  rendererDefaultImageScale: number;
  dashboardPerformanceMetrics: string[];
  panelSeriesLimit: number;
  reportRenderQueryGracePeriodMs: number;
  sqlConnectionLimits: SqlConnectionLimits;
  sharedWithMeFolderUID: string;
  rootFolderUID: string;
  localFileSystemAvailable: boolean;
  provisioningEnabled: boolean;
  cloudMigrationEnabled: boolean;
  cloudMigrationIsTarget: boolean;
  cloudMigrationPollIntervalMs: number;
  pluginCatalogURL: string;
  pluginAdminEnabled: boolean;
  pluginAdminExternalManageEnabled: boolean;
  pluginCatalogHiddenPlugins: string[];
  pluginCatalogManagedPlugins: string[];
  pluginCatalogPreinstalledPlugins: PreinstalledPlugin[];
  pluginCatalogPreinstalledAutoUpdate?: boolean;
  pluginsCDNBaseURL: string;
  tokenExpirationDayLimit: number;
  listDashboardScopesEndpoint: string;
  listScopesEndpoint: string;
  reportingStaticContext: Record<string, string>;
  exploreDefaultTimeOffset: string;
  exploreHideLogsDownload: boolean;
  quickRanges?: TimeOption[];
  pluginRestrictedAPIsAllowList?: Record<string, string[]>;
  pluginRestrictedAPIsBlockList?: Record<string, string[]>;
  openFeatureContext: Record<string, unknown>;

  // The namespace to use for kubernetes apiserver requests
  namespace: string;
  caching: {
    enabled: boolean;
    defaultTTLMs: number;
  };
  recordedQueries: {
    enabled: boolean;
  };
  reporting: {
    enabled: boolean;
  };
  analytics: {
    enabled: boolean;
    presenceIndicatorsDisabled?: boolean;
  };
}

interface SqlConnectionLimits {
  maxOpenConns: number;
  maxIdleConns: number;
  connMaxLifetime: number;
}

export interface AuthSettings {
  AuthProxyEnableLoginToken?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  OAuthSkipOrgRoleUpdateSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  SAMLSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  LDAPSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  JWTAuthSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  GrafanaComSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  GithubSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  GitLabSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  OktaSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  AzureADSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  GoogleSkipOrgRoleSync?: boolean;
  // @deprecated -- this is no longer used and will be removed in Grafana 11
  GenericOAuthSkipOrgRoleSync?: boolean;

  disableLogin?: boolean;
  basicAuthStrongPasswordPolicy?: boolean;
  disableSignoutMenu?: boolean;
}
